import { useContext, useCallback } from 'react';
import { useRouter } from 'next/router';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import formatAcademyEntryType from 'helpers/formatAcademyEntryType';
import { formatAcademyDateString } from 'helpers/date';
import { AcademyDetails, AcademyHero, M1ArticleModule, PageSpinner } from 'ui/components';

export const AcademyDetailsSection = ({ content, id, host = '', academyEntry }) => {
	const router = useRouter();
	const dictionary = useContext(DictionaryContext);

	// Guard against null dictionary or missing data
	if (!dictionary || !id || !academyEntry) {
		return <PageSpinner />;
	}

	// Helper to get localized academy type name
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const getLocalizedTypeName = useCallback(
		(typeName: string | undefined): string => {
			if (!typeName) return '';
			const normalizedTypeName = typeName.trim().replace(/\s+/g, '');
			return dictionary.getValue(`AcademyType.${normalizedTypeName}`, null, typeName);
		},
		[dictionary, router.locale],
	);

	// Helper to get localized language name
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const getLocalizedLanguageName = useCallback(
		(languageName: string | undefined): string => {
			if (!languageName) return '';
			return dictionary.getValue(`AcademyLanguage.${languageName}`, null, languageName);
		},
		[dictionary, router.locale],
	);

	// Helper to format date with current locale (month only)
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const getFormattedDate = useCallback(
		(dateString: string): string => {
			return formatAcademyDateString(dateString, router.locale || 'en');
		},
		[router.locale],
	);

	const academyEntryDictionary = {
		courseTitle: dictionary.getValue('Academy.EntryCourseTitle', null, 'Course Information'),
		topicTitle: dictionary.getValue('Academy.EntryTopicTitle', null, 'Topic'),
		languageTitle: dictionary.getValue('Academy.EntryLanguageTitle', null, 'Language'),
		timePlaceTitle: dictionary.getValue('Academy.EntryTimePlaceTitle', null, 'Time and Place'),
		startTimeTitle: dictionary.getValue('Academy.EntryStartTimeTitle', null, 'Start'),
		endTimeTitle: dictionary.getValue('Academy.EntryEndTimeTitle', null, 'End'),
		locationTitle: dictionary.getValue('Academy.EntryLocationTitle', null, 'Location'),
		additionalInfoTitle: dictionary.getValue('Academy.EntryAdditionalInfoTitle', null, 'Additional Information'),
		additionalInfoFilesLinkText: dictionary.getValue('Academy.EntryAdditionalInfoFilesLinkText', null, 'Download file'),
		additionalInfoLinkText: dictionary.getValue('Academy.EntryAdditionalInfoLinkText', null, 'Find more information from'),
		contactInformationTitle: dictionary.getValue('Academy.EntryContactInformationTitle', null, 'Contact Information'),
		hostText: dictionary.getValue('Academy.EntryHostText', null, 'Host:'),
		contactLinkText: dictionary.getValue('Academy.EntryContactLinkText', null, 'Contact us with questions'),
		contactHostLinkText: dictionary.getValue('Academy.EntryContactHostLinkText', null, 'Contact host'),
		locationLinkButtonText: dictionary.getValue('Academy.EntryLocationLinkButtonText', null, 'Get directions'),
		signUpLinkButtonText: dictionary.getValue('Academy.EntrySignUpLinkButtonText', null, 'Sign up here'),
		signUpLinkAriaLabelText: dictionary.getValue('Academy.EntrySignUpLinkAriaLabelText', null, 'Sign up to'),
	};

	// Create a Google Maps link based on latitude and longitude
	const locationLatitude = academyEntry?.location?.latitude;
	const locationLongitude = academyEntry?.location?.longitude;
	const transformedLocationLink =
		academyEntry?.location && locationLatitude && locationLongitude
			? `https://www.google.com/maps/place/${locationLatitude},${locationLongitude}`
			: '';

	// Treat academyEntry's host - remove empty link objects
	const hasValidHostLink = academyEntry?.host?.link?.url;
	const isEmail = typeof academyEntry?.host?.link?.url === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(academyEntry?.host?.link?.url);
	const transformedHost = academyEntry?.host
		? {
				name: academyEntry.host.name,
				link: hasValidHostLink
					? isEmail
						? {
								...academyEntry.host.link,
								url: `mailto:${academyEntry.host.link.url}`,
						  }
						: academyEntry.host.link
					: undefined,
		  }
		: undefined;

	// Treat academyEntry's contactInfoLink - remove empty link objects
	const hasValidContactInfoLink = academyEntry?.contactInfoLink?.url;
	const isContactInfoLinkEmail =
		typeof academyEntry?.contactInfoLink?.url === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(academyEntry?.contactInfoLink?.url);
	const transformedContactInfoLink = hasValidContactInfoLink
		? isContactInfoLinkEmail
			? {
					name: academyEntry?.contactInfoLink?.name,
					url: `mailto:${academyEntry?.contactInfoLink?.url}`,
			  }
			: academyEntry.contactInfoLink
		: undefined;

	const trimmedSignUpLink = academyEntry?.linkToSignUp?.trim();
	const transformedSignUpLink = trimmedSignUpLink
		? {
				url: trimmedSignUpLink,
				name: academyEntry?.academyLinkText || academyEntryDictionary.signUpLinkButtonText,
				target: '_blank',
		  }
		: undefined;

	const signUpAriaLabel = trimmedSignUpLink ? `${academyEntryDictionary.signUpLinkAriaLabelText} ${academyEntry?.title}` : undefined;

	// Get localized type and language
	const localizedTypeName = getLocalizedTypeName(academyEntry?.type?.name);

	const localizedLanguage =
		academyEntry?.language && academyEntry.language.name
			? {
					...academyEntry.language,
					displayName: getLocalizedLanguageName(academyEntry.language.name),
			  }
			: undefined;

	return (
		<>
			<AcademyHero
				dictionary={academyEntryDictionary}
				academyTitle={academyEntry?.title}
				academyType={formatAcademyEntryType(academyEntry?.type?.name)}
				academyTypeDisplayName={localizedTypeName}
				academyStartDateTime={academyEntry?.startDateTime}
				localizedStartDateTime={getFormattedDate(academyEntry?.startDateTime)}
				text={academyEntry?.teaserText}
				image={academyEntry?.image}
				link={transformedSignUpLink}
				signUpAriaLabel={signUpAriaLabel}
			/>
			<M1ArticleModule title={academyEntry?.title} subtitle={academyEntry?.teaserText} text={academyEntry?.description} />

			<AcademyDetails
				academyEntry={academyEntry}
				dictionary={academyEntryDictionary}
				topics={academyEntry?.topics}
				language={localizedLanguage}
				formattedStartDateTime={getFormattedDate(academyEntry?.startDateTime)}
				formattedEndDateTime={getFormattedDate(academyEntry?.endDateTime)}
				host={transformedHost}
				location={academyEntry?.hideLocation ? null : academyEntry?.location}
				googleMapsUrl={transformedLocationLink}
				contactInformation={transformedContactInfoLink || null}
				linkToSignUp={transformedSignUpLink}
				signUpAriaLabel={signUpAriaLabel}
				additionalInfoLink={academyEntry?.additionalInfoLink || null}
				additionalInfoFiles={academyEntry?.additionalInfoFiles || null}
			/>
		</>
	);
};
